import { RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

/** A font size in px: slider for quick changes, number field for exact ones. */
export function SizeField({
  label,
  hint,
  value,
  defaultValue,
  min,
  max,
  error,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  defaultValue: number;
  min: number;
  max: number;
  error?: string;
  onChange: (value: number) => void;
}) {
  const inRange = Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : defaultValue;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-sm font-medium">{label}</label>
        {value !== defaultValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-xs text-muted-foreground"
            onClick={() => onChange(defaultValue)}
            title={`Reset to ${defaultValue}px`}
          >
            <RotateCcw className="mr-1 h-3 w-3" /> Default
          </Button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Slider
          value={[inRange]}
          min={min}
          max={max}
          step={0.5}
          onValueChange={([v]) => onChange(v)}
          aria-label={label}
          className="flex-1"
        />
        <div className="relative w-20 shrink-0">
          <Input
            type="number"
            inputMode="decimal"
            min={min}
            max={max}
            step={0.5}
            value={Number.isFinite(value) ? value : ''}
            onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))}
            className={`h-9 pr-7 tabular-nums ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
            aria-invalid={!!error}
            aria-label={`${label} in pixels`}
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">px</span>
        </div>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
